import { useCallback } from 'react';
import { useXero } from '../contexts/XeroContext';
import { API_ENDPOINTS } from '../config/api';
import { useAuth } from '../hooks/useAuth';

interface XeroError extends Error {
  message: string;
}

export function useXeroApi() {
  const { accessToken, authenticate, checkAuthentication } = useXero();
  const { user } = useAuth();

  const callApi = useCallback(async (endpoint: string, options: RequestInit = {}) => {
    try {
      // Check authentication before making the call
      const isAuthenticated = await checkAuthentication();
      if (!isAuthenticated) {
        throw new Error('XERO_AUTH_REQUIRED');
      }

      const response = await fetch(endpoint, {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Try to refresh the token
          const refreshResponse = await fetch(API_ENDPOINTS.XERO.TOKEN_REFRESH, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ userId: user?.username }),
          });

          if (!refreshResponse.ok) {
            throw new Error('XERO_AUTH_REQUIRED');
          }

          // Retry the original request
          return callApi(endpoint, options);
        }
        throw new Error(`API call failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error: unknown) {
      const xeroError = error as XeroError;
      if (xeroError.message === 'XERO_AUTH_REQUIRED') {
        authenticate();
      }
      throw error;
    }
  }, [accessToken, authenticate, checkAuthentication, user]);

  const getProjects = useCallback(async (params?: { 
    page?: number; 
    pageSize?: number; 
    status?: string;
    contactId?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.contactId) queryParams.append('contactId', params.contactId);

    const url = `${API_ENDPOINTS.XERO.PROJECTS}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    return callApi(url);
  }, [callApi]);

  const createProject = useCallback(async (projectData: any) => {
    return callApi(API_ENDPOINTS.XERO.PROJECTS, {
      method: 'POST',
      body: JSON.stringify(projectData),
    });
  }, [callApi]);

  const updateProject = useCallback(async (projectId: string, projectData: any) => {
    return callApi(`${API_ENDPOINTS.XERO.PROJECTS}/${projectId}`, {
      method: 'PUT',
      body: JSON.stringify(projectData),
    });
  }, [callApi]);

  return {
    getProjects,
    createProject,
    updateProject,
  };
}
